<script setup lang="ts">
import {ref, watch} from "vue";
import Dialog from "primevue/dialog";
import Select from "primevue/select";
import Button from "primevue/button";
import TimeoffType from "@/components/timeoff/TimeoffType.vue";
import useTimeoffHelper from "@/composables/useTimeoffHelper.ts";
import {timeoffBackend} from "@/backend/timeoff.backend.ts";
import {useToast} from "primevue/usetoast";

interface Props {
  visible: boolean;
  timeoff: any;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:visible': [value: boolean];
  'updated': [timeoff: any];
}>();

const toast = useToast();
const helper = useTimeoffHelper();
const selectedType = ref<string>('');
const loading = ref(false);

// Watch for both timeoff id changes and dialog visibility
watch([() => props.timeoff?.id, () => props.visible], ([_newId, newVisible]) => {
  if (newVisible && props.timeoff) {
    selectedType.value = props.timeoff.type;
  }
});

async function saveType() {
  if (!props.timeoff || !selectedType.value) return;
  
  try {
    loading.value = true;
    const updated = await timeoffBackend.updateType(props.timeoff.id, selectedType.value);
    
    toast.add({
      severity: 'success',
      summary: 'Type Updated',
      detail: 'Time off type has been successfully updated.',
      life: 3000
    });
    
    emit('updated', updated);
    emit('update:visible', false);
  } catch (error) {
    console.error('Failed to update timeoff type:', error);
    toast.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: error instanceof Error ? error.message : 'Failed to update time off type.',
      life: 5000
    });
  } finally {
    loading.value = false;
  }
}

function cancel() {
  emit('update:visible', false);
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="emit('update:visible', $event)"
    modal
    header="Edit Time Off Type"
    :style="{ width: '30rem' }"
    :draggable="false"
  >
    <div class="flex flex-col gap-4 py-4">
      <div>
        <label class="block text-sm font-medium mb-2">Current Type</label>
        <TimeoffType v-if="timeoff" :value="timeoff.type" class="text-base" />
      </div>
      
      <div>
        <label for="newType" class="block text-sm font-medium mb-2">New Type</label>
        <Select
          id="newType"
          v-model="selectedType"
          :options="helper.editableTypes()"
          optionLabel="label"
          optionValue="value"
          placeholder="Select a type"
          class="w-full"
          :disabled="loading"
        >
          <template #option="slotProps">
            <TimeoffType :value="slotProps.option.value" />
          </template>
          <template #value="slotProps">
            <TimeoffType v-if="slotProps.value" :value="slotProps.value" :key="selectedType"/>
            <span v-else>{{ slotProps.placeholder }}</span>
          </template>
        </Select>
      </div>
      
      <div v-if="timeoff" class="text-sm text-muted-color">
        <div><strong>Dates:</strong> {{ helper.format(timeoff.start) }} - {{ helper.format(timeoff.end) }}</div>
        <div v-if="timeoff.comment" class="mt-1"><strong>Comment:</strong> {{ timeoff.comment }}</div>
      </div>
    </div>
    
    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          severity="secondary"
          @click="cancel"
          :disabled="loading"
        />
        <Button
          label="Save"
          @click="saveType"
          :loading="loading"
          :disabled="!selectedType || selectedType === timeoff?.type"
        />
      </div>
    </template>
  </Dialog>
</template>
